import axios from 'axios';
import { useState, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { apiClient, setAccessToken, getAccessToken } from '../../shared/api/client';

function decodeJwt(token: string): { sub?: string; email?: string } | null {
  try {
    const payload = token.split('.')[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

interface LoginCredentials {
  email: string;
  password: string;
}

export function useAuth() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => !!getAccessToken());

  const login = useCallback(async (credentials: LoginCredentials) => {
    const res = await apiClient.post<{ accessToken: string }>('/auth/admin/login', credentials);
    setAccessToken(res.data.accessToken);
    setIsAuthenticated(true);
    const claims = decodeJwt(res.data.accessToken);
    if (claims?.sub) {
      Sentry.setUser({ id: claims.sub, email: claims.email });
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiClient.post('/auth/admin/logout');
    } finally {
      setAccessToken(null);
      setIsAuthenticated(false);
      Sentry.setUser(null);
    }
  }, []);

  const initAuth = useCallback(async (): Promise<boolean> => {
    // Try silent refresh on app startup
    try {
      const res = await axios.post<{ accessToken: string }>('/api/auth/admin/refresh', {}, { withCredentials: true });
      setAccessToken(res.data.accessToken);
      setIsAuthenticated(true);
      const claims = decodeJwt(res.data.accessToken);
      if (claims?.sub) {
        Sentry.setUser({ id: claims.sub, email: claims.email });
      }
      return true;
    } catch {
      setIsAuthenticated(false);
      Sentry.setUser(null);
      return false;
    }
  }, []);

  return { isAuthenticated, login, logout, initAuth };
}
